"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BlockWrapper, ButtonGroup, MarkdownRenderer } from "@/shared/components";
import type { CommonSlideDataType } from "@/shared/types";
import { displayFontClassName } from "@/lib/fonts";
import type { BlockSectionBg } from "./common/types";
import { getMarkdownProseClass, sectionBgToWrapper } from "./blockSection";
import { getSliderThemeClasses } from "./sliderTheme";
import { cn } from "@/lib/utils";
import "swiper/css";

interface SliderBlockProps {
  slides?: CommonSlideDataType[];
  sectionBg?: BlockSectionBg;
  layout?: "half_half" | "two_thirds" | string | null;
}

/** Mobile/tablet: фиксированное соотношение; desktop: заполняет колонку целиком. */
const SLIDE_IMAGE_CLASS =
  "relative w-full overflow-hidden aspect-[375/280] md:aspect-auto md:min-h-[350px] desktop:absolute desktop:inset-0 desktop:aspect-auto desktop:h-full desktop:min-h-0";

function resolveSliderDesktopLayout(
  layout: SliderBlockProps["layout"]
): "half_half" | "two_thirds" {
  return layout === "two_thirds" ? "two_thirds" : "half_half";
}

export function SliderBlock({
  slides = [],
  sectionBg = "light",
  layout = "half_half",
}: SliderBlockProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);

  if (slides.length === 0) return null;

  const desktopLayout = resolveSliderDesktopLayout(layout);
  const theme = getSliderThemeClasses(sectionBg);
  const descriptionClassName = cn(
    "font-sans text-base leading-[1.2] [&_a]:text-link [&_p+p]:mt-4",
    getMarkdownProseClass(sectionBg)
  );
  const hasMultipleSlides = slides.length > 1;

  const handleSlideChange = (swiper: SwiperType) => {
    setFading(true);
    setTimeout(() => {
      setActiveIndex(swiper.realIndex);
      setFading(false);
    }, 200);
  };

  const goToSlide = (index: number) => {
    swiperRef.current?.slideToLoop(index);
  };

  const goToPreviousSlide = () => {
    swiperRef.current?.slidePrev();
  };

  const goToNextSlide = () => {
    swiperRef.current?.slideNext();
  };

  const renderPagination = (className?: string) => (
    <div
      className={cn(
        "flex h-2 items-center gap-4 desktop:gap-2",
        className
      )}
      role="tablist"
      aria-label="Слайды"
    >
      {slides.map((_, index) => (
        <button
          key={index}
          type="button"
          role="tab"
          aria-selected={index === activeIndex}
          aria-label={`Слайд ${index + 1}`}
          onClick={() => goToSlide(index)}
          className={cn(
            "size-2 rounded-full border transition-colors duration-300",
            index === activeIndex
              ? theme.paginationDotActive
              : theme.paginationDotInactive,
            index !== activeIndex && "hover:opacity-70"
          )}
        />
      ))}
    </div>
  );

  const renderSlideImage = (slide: CommonSlideDataType, priority: boolean) => {
    if (!slide.image) {
      return <div className={cn(SLIDE_IMAGE_CLASS, "bg-muted")} />;
    }

    const imageSizes =
      desktopLayout === "two_thirds"
        ? "(max-width: 1439px) 100vw, 33vw"
        : "(max-width: 1439px) 100vw, 50vw";

    return (
      <div className={SLIDE_IMAGE_CLASS}>
        <Image
          className="object-cover"
          src={slide.image.src}
          alt={slide.image.alt || slide.headline || ""}
          title={slide.image.title ?? undefined}
          fill
          sizes={imageSizes}
          priority={priority}
        />
      </div>
    );
  };

  return (
    <BlockWrapper noPadding bgColor={sectionBgToWrapper(sectionBg)}>
      <div className="flex w-full flex-col desktop:flex-row desktop:items-stretch">
        <div
          className={cn(
            "relative flex w-full flex-col gap-10",
            "px-4 py-10 md:px-6 md:pt-10 md:pb-12",
            "desktop:gap-20 desktop:px-10 desktop:py-20",
            desktopLayout === "two_thirds" ? "desktop:w-2/3" : "desktop:w-1/2",
            theme.section,
            theme.border,
            "border-b desktop:border-b-0 desktop:border-r"
          )}
        >
          {/*
            Все слайды в одной ячейке grid — высота блока = самый высокий слайд.
            items-start: слайды не растягиваются, кнопка остаётся сразу под текстом.
          */}
          <div className="grid w-full items-start">
            {slides.map((slide, index) => (
              <div
                key={slide.id || index}
                className={cn(
                  "col-start-1 row-start-1 flex w-full flex-col gap-10",
                  index !== activeIndex && "invisible pointer-events-none",
                  index === activeIndex && "transition-opacity duration-200",
                  index === activeIndex && (fading ? "opacity-0" : "opacity-100")
                )}
                aria-hidden={index !== activeIndex}
              >
                <div className="flex flex-col gap-6 md:gap-6">
                  <h2
                    className={cn(
                      displayFontClassName,
                      "text-heading-2-sm font-bold leading-[1.2] md:text-heading-2",
                      theme.headline
                    )}
                  >
                    {slide.headline}
                  </h2>

                  {slide.description && (
                    <MarkdownRenderer
                      content={slide.description}
                      className={descriptionClassName}
                    />
                  )}
                </div>

                {slide.buttons && slide.buttons.length > 0 && (
                  <ButtonGroup items={slide.buttons} className="flex-wrap" />
                )}
              </div>
            ))}
          </div>

          {hasMultipleSlides && renderPagination("shrink-0")}
        </div>

        <div
          className={cn(
            "relative w-full overflow-hidden",
            "desktop:min-h-0 desktop:self-stretch",
            desktopLayout === "two_thirds" ? "desktop:w-1/3" : "desktop:w-1/2"
          )}
        >
          {hasMultipleSlides ? (
            <>
              <Swiper
                modules={[Autoplay]}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                onSlideChange={handleSlideChange}
                autoplay={{
                  delay: 4500,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                loop
                spaceBetween={0}
                slidesPerView={1}
                className="h-full w-full desktop:absolute desktop:inset-0"
              >
                {slides.map((slide, index) => (
                  <SwiperSlide
                    key={slide.id || index}
                    className="relative !h-auto desktop:!h-full"
                  >
                    {renderSlideImage(slide, index === 0)}
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-4 md:hidden">
                <button
                  type="button"
                  onClick={goToPreviousSlide}
                  className={cn(
                    "pointer-events-auto flex size-10 items-center justify-center",
                    theme.arrow
                  )}
                  aria-label="Предыдущий слайд"
                >
                  <ChevronLeft className="size-6" strokeWidth={1.5} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={goToNextSlide}
                  className={cn(
                    "pointer-events-auto flex size-10 items-center justify-center",
                    theme.arrow
                  )}
                  aria-label="Следующий слайд"
                >
                  <ChevronRight className="size-6" strokeWidth={1.5} aria-hidden />
                </button>
              </div>
            </>
          ) : (
            renderSlideImage(slides[0], true)
          )}
        </div>
      </div>
    </BlockWrapper>
  );
}
